import { useState } from "react";
import { Helmet } from "react-helmet";
import Navigation from "@/components/Navigation";
import Breadcrumb from "@/components/Breadcrumb";
import SchemaMarkup from "@/components/SchemaMarkup";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Heart, Briefcase, Users } from "lucide-react";
import { createWebApplicationSchema } from "@/utils/schemaHelpers";

const Compatibility = () => {
  const [sign1, setSign1] = useState("");
  const [sign2, setSign2] = useState("");
  const [showResult, setShowResult] = useState(false);

  const appSchema = createWebApplicationSchema(
    "Test de Compatibilidad Zodiacal Gratis",
    "Descubre la compatibilidad entre signos del zodiaco en amor, amistad y trabajo. Test gratuito de compatibilidad astrológica.",
    "LifestyleApplication"
  );

  const calculateCompatibility = () => {
    if (sign1 && sign2) {
      setShowResult(true);
    }
  };

  const getCompatibilityScore = () => {
    return Math.floor(Math.random() * 30) + 70; // 70-100% para resultados positivos
  };

  return (
    <>
      <Helmet>
        <title>Test de Compatibilidad Zodiacal Gratis - Amor y Amistad | Dstino</title>
        <meta name="description" content="Test de compatibilidad entre signos del zodiaco gratis. Descubre tu compatibilidad en amor, amistad y trabajo con cualquier signo zodiacal." />
        <meta property="og:title" content="Test de Compatibilidad Zodiacal Gratis" />
        <meta property="og:description" content="Descubre la compatibilidad entre signos del zodiaco en amor, amistad y trabajo." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${window.location.origin}/compatibilidad`} />
      </Helmet>

      <SchemaMarkup schema={appSchema} />

      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="pt-24 pb-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <Breadcrumb 
              items={[
                { name: "Inicio", url: "/" },
                { name: "Compatibilidad" }
              ]}
            />

            <h1 className="text-5xl font-bold text-center mb-4 text-glow">
            Test de Compatibilidad Zodiacal
          </h1>
          <p className="text-center text-xl text-muted-foreground mb-12">
            Descubre tu compatibilidad en amor, amistad y trabajo
          </p>

          <Card className="p-8 gradient-celestial mb-8">
            <h2 className="text-2xl font-bold mb-6 text-center">
              Selecciona los Signos
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block mb-3 font-semibold">Primer Signo</label>
                <Select value={sign1} onValueChange={setSign1}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Elige tu signo" />
                  </SelectTrigger>
                  <SelectContent>
                    {zodiacSigns.map((sign) => (
                      <SelectItem key={sign.id} value={sign.id}>
                        {sign.emoji} {sign.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block mb-3 font-semibold">Segundo Signo</label>
                <Select value={sign2} onValueChange={setSign2}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Elige el otro signo" />
                  </SelectTrigger>
                  <SelectContent>
                    {zodiacSigns.map((sign) => (
                      <SelectItem key={sign.id} value={sign.id}>
                        {sign.emoji} {sign.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button 
              onClick={calculateCompatibility} 
              className="w-full"
              size="lg"
              disabled={!sign1 || !sign2}
            >
              Calcular Compatibilidad
            </Button>
          </Card>

          {showResult && sign1 && sign2 && (
            <Card className="p-8 gradient-mystical animate-in fade-in duration-500">
              <div className="text-center mb-8">
                <div className="flex justify-center items-center gap-4 mb-4">
                  <span className="text-6xl">
                    {zodiacSigns.find(s => s.id === sign1)?.emoji}
                  </span>
                  <Heart className="w-12 h-12 text-accent animate-pulse" />
                  <span className="text-6xl">
                    {zodiacSigns.find(s => s.id === sign2)?.emoji}
                  </span>
                </div>
                <h3 className="text-3xl font-bold">
                  {zodiacSigns.find(s => s.id === sign1)?.name} & {zodiacSigns.find(s => s.id === sign2)?.name}
                </h3>
              </div>

              <div className="space-y-6">
                <div className="bg-card/50 backdrop-blur rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Heart className="w-8 h-8 text-accent" />
                    <h4 className="text-2xl font-bold">Amor</h4>
                  </div>
                  <div className="mb-3">
                    <div className="flex justify-between mb-2">
                      <span>Compatibilidad</span>
                      <span className="font-bold text-accent">{getCompatibilityScore()}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-3">
                      <div 
                        className="bg-accent h-3 rounded-full transition-all duration-1000"
                        style={{ width: `${getCompatibilityScore()}%` }}
                      />
                    </div>
                  </div>
                  <p className="text-muted-foreground">
                    Vuestra conexión romántica es intensa y apasionada. Compartís valores 
                    similares y una profunda comprensión emocional que fortalece el vínculo.
                  </p>
                </div>

                <div className="bg-card/50 backdrop-blur rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Users className="w-8 h-8 text-primary" />
                    <h4 className="text-2xl font-bold">Amistad</h4>
                  </div>
                  <div className="mb-3">
                    <div className="flex justify-between mb-2">
                      <span>Compatibilidad</span>
                      <span className="font-bold text-primary">{getCompatibilityScore()}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-3">
                      <div 
                        className="bg-primary h-3 rounded-full transition-all duration-1000"
                        style={{ width: `${getCompatibilityScore()}%` }}
                      />
                    </div>
                  </div>
                  <p className="text-muted-foreground">
                    Formáis un dúo dinámico como amigos. Vuestra energía se complementa 
                    perfectamente y siempre encontráis formas de divertiros juntos.
                  </p>
                </div>

                <div className="bg-card/50 backdrop-blur rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Briefcase className="w-8 h-8 text-secondary" />
                    <h4 className="text-2xl font-bold">Trabajo</h4>
                  </div>
                  <div className="mb-3">
                    <div className="flex justify-between mb-2">
                      <span>Compatibilidad</span>
                      <span className="font-bold text-secondary">{getCompatibilityScore()}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-3">
                      <div 
                        className="bg-secondary h-3 rounded-full transition-all duration-1000"
                        style={{ width: `${getCompatibilityScore()}%` }}
                      />
                    </div>
                  </div>
                  <p className="text-muted-foreground">
                    Como compañeros de trabajo, vuestras habilidades se complementan 
                    brillantemente. Juntos podéis lograr grandes cosas en el ámbito profesional.
                  </p>
                </div>
              </div>
            </Card>
          )}
          </div>
        </main>
      </div>
    </>
  );
};

const zodiacSigns = [
  { id: "aries", name: "Aries", emoji: "♈" },
  { id: "tauro", name: "Tauro", emoji: "♉" },
  { id: "geminis", name: "Géminis", emoji: "♊" },
  { id: "cancer", name: "Cáncer", emoji: "♋" },
  { id: "leo", name: "Leo", emoji: "♌" },
  { id: "virgo", name: "Virgo", emoji: "♍" },
  { id: "libra", name: "Libra", emoji: "♎" },
  { id: "escorpio", name: "Escorpio", emoji: "♏" },
  { id: "sagitario", name: "Sagitario", emoji: "♐" },
  { id: "capricornio", name: "Capricornio", emoji: "♑" },
  { id: "acuario", name: "Acuario", emoji: "♒" },
  { id: "piscis", name: "Piscis", emoji: "♓" },
];

export default Compatibility;
